import React, { memo } from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import cn from 'classnames';
import DayPicker, { DateUtils } from 'react-day-picker';
import Grid from '@material-ui/core/Grid';
import { last } from 'lodash';
import { lighten } from '@material-ui/core/styles';
import { useTranslation } from '../../../src/i18n';
import FormikAutocompleteSelect from '../../../components/Fields/FormikAutocompleteSelect';
import { TP } from '../../../constants/consts';
import { yearsToShowCountOptions } from '../../../utils/common';
import Title from '../../../components/Title';
import Button from '../../../components/Button';
import { borderRadius, colors } from '../../../constants/styleConstants';
import { createDate } from '../../../utils/date';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    maxHeight: 'calc(100vh - 170px)',
    overflowY: 'auto',
    marginTop: theme.spacing(1),
  },
  yearField: {
    width: '100%',
    maxWidth: 100,
    [theme.breakpoints.down('sm')]: {
      marginTop: '1rem',
      maxWidth: '100%',
    },
  },
  buttonsBlock: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  countBlock: {
    fontWeight: 'bold',
    backgroundColor: 'rgba(243, 109, 42, 0.5)',
    padding: '0.73rem',
    borderRadius: borderRadius.button,
    lineHeight: 1.75,
    [theme.breakpoints.down('sm')]: {
      marginBottom: '1rem',
    },
  },
  calendar: {
    width: '100%',
    '& .DayPicker-WeekdaysRow': {
      borderBottom: `1px dotted ${colors.title}`,
      paddingBottom: 10,
    },
    '& .DayPicker-Day': {
      padding: 12,
      lineHeight: 1,
      '&--today': {
        color: `${colors.primary} !important`,
      },
      '&--selected:not(.DayPicker-Day--outside)': {
        backgroundColor: `${colors.primary} !important`,
        color: `${colors.white} !important`,
      },
    },
  },
  orangeDisabled: {
    '& .DayPicker-Day--disabled': {
      backgroundColor: `${lighten(colors.primary, 0.5)} !important`,
      color: `${colors.white} !important`,
    },
    '& .DayPicker-Day--outside': {
      backgroundColor: `${colors.white} !important`,
      color: `${colors.white} !important`,
    },
  },
  caption: {
    display: 'table-caption',
    textAlign: 'center',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
}));

const Weekday = ({ weekday, className, localeUtils, locale }) => {
  const weekdayName = localeUtils.formatWeekdayShort(weekday, locale);
  return (
    <div className={className} title={weekdayName}>
      {weekdayName.slice(0, 1)}
    </div>
  );
};

Weekday.propTypes = {
  weekday: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  localeUtils: PropTypes.any,
  locale: PropTypes.any,
};

const Caption = ({ date, className }) => <span className={className}>{createDate(date).format('MMMM')}</span>;

Caption.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  className: PropTypes.string,
};
const EditProductionCalendar = ({ submitDates, chosenDates, disabledDates, highlightDisabled }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = React.useState([]);
  const [disabledSelectedDays, setDisabledSelectedDays] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const [years] = React.useState(yearsToShowCountOptions(1919)); // the year when productions strats added to operabase (earliestYearOfMusicalWork is used only for MW premiere date)

  React.useEffect(() => {
    const dateFormatedChosenDates = chosenDates.map(d => new Date(d));
    const dateFormatedDisabledDates = disabledDates.map(d => new Date(d));
    const newestDate = last(
      [...dateFormatedChosenDates, ...dateFormatedDisabledDates].sort((a, b) => a.getTime() - b.getTime()),
    );
    setSelectedDays(dateFormatedChosenDates);
    setDisabledSelectedDays(dateFormatedDisabledDates);
    setSelectedYear((newestDate || new Date()).getFullYear());
  }, [chosenDates, disabledDates]);

  const handleDayClick = (day, { selected, disabled }) => {
    if (!disabled) {
      const newDays = [...selectedDays];
      if (selected) {
        const selectedIndex = newDays.findIndex(selectedDay => DateUtils.isSameDay(selectedDay, day));
        newDays.splice(selectedIndex, 1);
      } else {
        newDays.push(day);
      }
      setSelectedDays(newDays);
    }
  };

  return (
    <>
      <Grid container justify="space-between" spacing={2}>
        <Grid sm={6} xs={12} item style={{ textAlign: 'left' }}>
          <Title>{t(`${TP}.FN_SELECT_DATES_FOR_PRODUCTION`)}</Title>
          <div>
            <FormikAutocompleteSelect
              className={classes.yearField}
              instanceId="edit-production-year"
              label={t(`${TP}.FN_SELECT_YEAR`)}
              field={{
                name: 'year',
                value: { value: selectedYear, label: selectedYear },
              }}
              onChange={v => setSelectedYear(v?.value)}
              options={years}
              withFiltering
            />
          </div>
        </Grid>
        <Grid sm={6} xs={12} item className={classes.buttonsBlock}>
          <div className={classes.countBlock}>{`${(selectedDays?.length || 0) +
            (disabledSelectedDays?.length || 0)} ${t(`${TP}.FN_SELECTED_DATES`)}`}</div>
          <Button onClick={() => submitDates(selectedDays)}>{t(`${TP}.m_ADD`)}</Button>
        </Grid>
      </Grid>
      <div className={classes.root}>
        <DayPicker
          month={new Date(`${selectedYear}/01/01`)}
          selectedDays={selectedDays}
          disabledDays={disabledSelectedDays}
          onDayClick={handleDayClick}
          numberOfMonths={12}
          canChangeMonth={false}
          captionElement={<Caption className={classes.caption} />}
          weekdayElement={<Weekday />}
          className={cn(classes.calendar, { [classes.orangeDisabled]: highlightDisabled })}
        />
      </div>
    </>
  );
};

EditProductionCalendar.propTypes = {
  submitDates: PropTypes.func,
  chosenDates: PropTypes.array,
  disabledDates: PropTypes.array,
};

EditProductionCalendar.defaultProps = {
  chosenDates: [],
  disabledDates: [],
  submitDates: () => {},
};

export default memo(EditProductionCalendar);
